import { useEffect, useId, useRef, useState, type CSSProperties, type Ref } from 'react';
import { flushSync } from 'react-dom';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { blurbDefault, clean, shortName, stagger, stripHtml, toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { mentionsTeam, reviewerName, reviewMonth, reviewRating, sortReviews, sourceLabel, type ReviewSort } from '../utils/reviews';
import { PATHS, topReviews, tourCta, useSiteFlags, type Review } from '../utils/site';

/** Cards per page; View More appends the next page in place. */
const PAGE = 8;

const tourCtaDef: SlotGroupDefinition = {
  label: 'Band Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const plansCtaDef: SlotGroupDefinition = {
  label: 'Band Floor Plans Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function Stars({ rating }: { rating: number }) {
  const n = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <div className="text-sm leading-4 tracking-[.08em] max-[767px]:text-[13px]" role="img" aria-label={`${rating} out of 5 stars`}>
      <span className="text-[--q-secondary]">{'★'.repeat(n)}</span>
      <span className="text-[--q-border]">{'★'.repeat(5 - n)}</span>
    </div>
  );
}

interface CardProps {
  review: Review;
  className?: string;
  style?: CSSProperties;
  reveal: boolean;
  cardRef?: Ref<HTMLElement>;
}

function ReviewCard({ review: r, className = '', style, reveal, cardRef }: CardProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => {
      if (el.dataset.open !== 'true') setClamped(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toggle = () => {
    const el = textRef.current;
    if (!el) return setOpen((o) => !o);
    const from = el.offsetHeight;
    flushSync(() => setOpen((o) => !o));
    const to = el.offsetHeight;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.animate?.(reduced ? [{ opacity: 0.4 }, { opacity: 1 }] : [{ height: `${from}px` }, { height: `${to}px` }], {
      duration: reduced ? 150 : 300,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    });
  };

  const badge = sourceLabel(r.source);
  const name = reviewerName(r.name) || (badge ? `A ${badge} reviewer` : 'A reviewer');
  const month = reviewMonth(r.date);

  return (
    <article
      ref={cardRef}
      tabIndex={-1}
      className={`flex min-w-0 flex-col gap-3.5 border-t border-[--q-border] pb-7 pt-6 focus-visible:outline-offset-4 max-[1199px]:pb-[26px] max-[1199px]:pt-[22px] max-[767px]:gap-3 max-[767px]:pb-6 max-[767px]:pt-5 ${className}`}
      style={style}
      data-reveal={reveal ? '' : undefined}
    >
      <div className="flex h-[22px] shrink-0 items-center justify-between">
        <Stars rating={reviewRating(r)} />
        {badge && (
          <span className="inline-flex h-[22px] items-center rounded-full border border-[--q-border] bg-[--q-paper] px-2.5 text-xs font-medium leading-4">{badge}</span>
        )}
      </div>
      <div
        ref={textRef}
        data-open={open}
        className={`overflow-hidden font-heading text-[20px] leading-[30px] max-[767px]:text-[18px] max-[767px]:leading-[27px] ${open ? '' : 'line-clamp-4'}`}
      >
        {stripHtml(r.text)}
      </div>
      {clamped && (
        <button
          type="button"
          className="self-start text-sm font-medium leading-5 text-[--q-primary-on-paper] underline decoration-1 underline-offset-[3px]"
          aria-expanded={open}
          onClick={toggle}
        >
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
      <div className="flex items-baseline gap-1.5 text-[13px] leading-[18px]">
        <span className="font-medium">{name}</span>
        {month && <span className="text-[--q-muted]">· {month}</span>}
      </div>
    </article>
  );
}

/** Every four-star-and-up review, newest first, eight at a time; hides when there are none. */
export function ReviewsList() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const flags = useSiteFlags();
  const selectId = useId();
  const all = useCollectionBinding<Review>('reviewslist.items', { collection: 'reviews' });
  const tour = useSlotGroup('reviewslist.aha_tour_cta', tourCtaDef);
  const plans = useSlotGroup('reviewslist.aha_plans_cta', plansCtaDef);
  const tourLink = tourCta(tour, flags.selfTourUrl);

  const sortLabel = useContentToken('reviewslist.sort_label', { defaultValue: 'Sort by' });
  const newestLabel = useContentToken('reviewslist.sort_newest', { defaultValue: 'Newest' });
  const ratedLabel = useContentToken('reviewslist.sort_rated', { defaultValue: 'Highest rated' });
  const teamLabel = useContentToken('reviewslist.sort_team', { defaultValue: 'Mentions the team' });
  const moreLabel = useContentToken('reviewslist.more_label', { defaultValue: 'View More Reviews' });

  const [sort, setSort] = useState<ReviewSort>('newest');
  const [pages, setPages] = useState(1);
  const [run, setRun] = useState(0);
  const [fading, setFading] = useState(false);
  const focusAt = useRef<number | null>(null);
  const cardRefs = useRef<Array<HTMLElement | null>>([]);

  const qualifying = topReviews(all);
  const anyTeam = qualifying.some(mentionsTeam);
  const list = sortReviews(qualifying, sort === 'team' && !anyTeam ? 'newest' : sort);
  const shown = editing ? list : list.slice(0, pages * PAGE);
  const lastPageStart = (pages - 1) * PAGE;

  useEffect(() => {
    if (focusAt.current == null) return;
    cardRefs.current[focusAt.current]?.focus();
    focusAt.current = null;
  }, [pages]);

  if (!qualifying.length && !editing) return null;

  const changeSort = (next: ReviewSort) => {
    setFading(true);
    window.setTimeout(() => {
      setSort(next);
      setPages(1);
      setRun((n) => n + 1);
      setFading(false);
    }, 150);
  };

  const showMore = () => {
    focusAt.current = shown.length;
    setPages((n) => n + 1);
  };

  const name = shortName(clean(p.property_name));
  const ahaDefault = toParagraph(name ? `Reading is one thing. See ${name} for yourself.` : undefined);
  // The band sits after the second row (card six; card five on phones), and only when more cards follow it.
  const bandClass = shown.length > 6 ? 'flex' : shown.length > 5 ? 'hidden max-[767px]:flex' : 'hidden';

  return (
    <section
      className="flex flex-col gap-12 bg-[--q-surface] px-[--gutter] pb-[88px] pt-24 max-[1199px]:gap-9 max-[1199px]:pb-16 max-[1199px]:pt-[72px] max-[767px]:gap-7 max-[767px]:py-14"
      id="reviews"
    >
      <div className="flex max-w-[760px] flex-col gap-4 max-[1199px]:max-w-[640px] max-[1199px]:gap-3.5 max-[767px]:gap-3" data-reveal>
        <RichTextContent path="reviewslist.eyebrow" as="div" className="eyebrow max-[767px]:text-[11px] max-[767px]:leading-[14px]" />
        <RichTextContent
          path="reviewslist.title"
          as="h2"
          className="h2 [&.h2]:text-[48px] [&.h2]:leading-[54px] max-[1199px]:[&.h2]:text-[40px] max-[1199px]:[&.h2]:leading-[46px] max-[767px]:[&.h2]:text-[34px] max-[767px]:[&.h2]:leading-[38px]"
        />
        <RichTextContent
          path="reviewslist.lead"
          as="p"
          className="m-0 text-lg leading-7 text-[--q-muted] empty:hidden max-[1199px]:text-base max-[1199px]:leading-[26px] max-[767px]:text-[15px] max-[767px]:leading-[23px]"
          defaultValue={blurbDefault(p.property_reviews_blurb, 'body', { paragraph: 1 })}
        />
      </div>

      {(qualifying.length > 1 || editing) && (
        <div className="-mb-4 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5 max-[767px]:mb-0 max-[767px]:flex-col max-[767px]:items-stretch">
          <label htmlFor={selectId} className="text-[13px] font-medium leading-[18px] text-[--q-muted]">
            <EditableToken path="reviewslist.sort_label">{sortLabel || 'Sort by'}</EditableToken>
          </label>
          <div className="relative flex h-11 w-[220px] items-center rounded-md border border-[--q-border] bg-[--q-paper] focus-within:[outline:2px_solid_var(--q-primary-on-paper)] focus-within:outline-offset-2 max-[767px]:h-12 max-[767px]:w-full">
            <select
              id={selectId}
              className="h-full w-full cursor-pointer appearance-none bg-transparent py-0 pl-4 pr-9 text-[15px] font-medium leading-5 text-[--q-ink] [-webkit-appearance:none] [border:0] focus-visible:[outline:none]"
              value={sort}
              onChange={(e) => changeSort(e.target.value as ReviewSort)}
            >
              <option value="newest">{newestLabel || 'Newest'}</option>
              <option value="rated">{ratedLabel || 'Highest rated'}</option>
              {anyTeam && <option value="team">{teamLabel || 'Mentions the team'}</option>}
            </select>
            <span className="pointer-events-none absolute right-3.5 text-sm leading-[14px]" aria-hidden="true">
              ▾
            </span>
          </div>
          {editing && (
            <div className="flex basis-full justify-end gap-3 text-xs text-[--q-muted]">
              Sort options:
              <EditableToken path="reviewslist.sort_newest">{newestLabel || 'Newest'}</EditableToken>
              <EditableToken path="reviewslist.sort_rated">{ratedLabel || 'Highest rated'}</EditableToken>
              <EditableToken path="reviewslist.sort_team">{teamLabel || 'Mentions the team'}</EditableToken>
            </div>
          )}
        </div>
      )}

      <EditableCollectionBinding
        bindingPath="reviewslist.items"
        label="Reviews"
        className={`grid grid-cols-[repeat(3,minmax(0,1fr))] gap-x-6 [transition:opacity_var(--q-motion-fast)_var(--q-ease-out)] max-[1199px]:grid-cols-[repeat(2,minmax(0,1fr))] max-[767px]:grid-cols-[minmax(0,1fr)] ${fading ? 'opacity-0' : ''}`}
      >
        {shown.map((r, i) => {
          const animated = run > 0 || i >= PAGE;
          const at = animated ? i - (i >= lastPageStart ? lastPageStart : 0) : i % 3;
          return (
            <ReviewCard
              key={`${run}-${i}`}
              review={r}
              reveal={!animated}
              cardRef={(el) => {
                cardRefs.current[i] = el;
              }}
              className={animated ? 'animate-[rise_var(--q-motion-reveal)_var(--q-ease-out)_both] [animation-delay:calc(var(--i)*var(--q-motion-stagger))]' : ''}
              style={{ ...stagger(at), order: i * 2 }}
            />
          );
        })}
        <div
          className={`${bandClass} col-span-full order-[11] mb-6 mt-2 items-center justify-between gap-12 rounded-lg bg-[--q-primary] px-10 py-8 text-[--q-on-primary] [&_.btn-on-image-ghost]:border-[--q-on-primary] [&_.btn-on-image-ghost]:text-[--q-on-primary] [&_a:focus-visible]:outline-[--q-accent-on-primary] max-[1199px]:flex-col max-[1199px]:items-start max-[1199px]:gap-6 max-[767px]:order-[9] max-[767px]:items-stretch max-[767px]:gap-5 max-[767px]:px-5 max-[767px]:py-6`}
          data-reveal
        >
          <RichTextContent
            path="reviewslist.aha_title"
            as="h3"
            className="m-0 font-heading text-[28px] font-light leading-[34px] [&_p]:m-0 max-[767px]:text-[24px] max-[767px]:leading-[30px]"
            defaultValue={ahaDefault}
          />
          <div className="flex shrink-0 gap-3 max-[767px]:flex-col max-[767px]:gap-2.5">
            <EditableSlotGroup groupPath="reviewslist.aha_tour_cta" definition={tourCtaDef}>
              <a
                className="btn btn-on-image"
                href={tourLink.href}
                target={tourLink.isTour ? tour._target || '_self' : '_self'}
                data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
              >
                {tourLink.label}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="reviewslist.aha_plans_cta" definition={plansCtaDef}>
              <a className="btn btn-on-image-ghost" href={plans.url || PATHS.floorPlans} target={plans._target || '_self'}>
                {plans.label || 'Explore Floor Plans'} <span className="btn__arrow">→</span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </EditableCollectionBinding>

      {shown.length < list.length && (
        <div className="flex justify-center pt-2 max-[767px]:pt-1">
          <button className="btn btn-secondary [&.btn]:px-7" type="button" onClick={showMore}>
            <EditableToken path="reviewslist.more_label">{moreLabel || 'View More Reviews'}</EditableToken>
          </button>
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsList',
  displayName: 'Reviews List',
  tokens: {
    'reviewslist.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Resident and visitor reviews</p>' },
    'reviewslist.title': { label: 'Title', type: 'richtext', defaultValue: '<p>In their own words.</p>' },
    'reviewslist.lead': {
      label: 'Lead',
      description: 'Until edited, shows the second paragraph of the reviews blurb. Pick the blurb field from the suggestions to show it in full.',
      type: 'richtext',
      defaultValue: '',
      suggestedFields: ['property_reviews_blurb'],
    },
    'reviewslist.sort_label': { label: 'Sort Label', type: 'text', defaultValue: 'Sort by' },
    'reviewslist.sort_newest': { label: 'Sort Option: Newest', type: 'text', defaultValue: 'Newest' },
    'reviewslist.sort_rated': { label: 'Sort Option: Highest Rated', type: 'text', defaultValue: 'Highest rated' },
    'reviewslist.sort_team': {
      label: 'Sort Option: Mentions the Team',
      description: 'Shown only when at least one review mentions the leasing or maintenance team.',
      type: 'text',
      defaultValue: 'Mentions the team',
    },
    'reviewslist.more_label': { label: 'View More Button Text', type: 'text', defaultValue: 'View More Reviews' },
    'reviewslist.aha_title': {
      label: 'Band Title',
      description: 'The band inside the list after the second row of reviews, shown only when more reviews follow it.',
      type: 'richtext',
      defaultValue: '<p>Reading is one thing. See <span data-type="field-token" data-field="property_name"></span> for yourself.</p>',
    },
  },
  slotGroups: {
    'reviewslist.aha_tour_cta': tourCtaDef,
    'reviewslist.aha_plans_cta': plansCtaDef,
  },
  collectionBindings: {
    'reviewslist.items': {
      label: 'Reviews',
      collection: 'reviews',
    },
  },
};

(ReviewsList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ReviewsList;
